import { useCallback, useRef, useState } from 'preact/hooks';

import { type AD_NETWORK } from '@/shared/api/user/types';
import { showPopup } from '@/shared/helpers/telegram/show-popup';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';
import { useUserProfile } from '@/shared/stores/app-store/app-store';

import { type UseAdvertisementProps } from './types';
import { useAdvertisement } from './use-advertisements';

interface UsePrioritizedAdvertisementProps extends UseAdvertisementProps {
  onAllNetworksFailed?: () => void;
}

export const usePrioritizedAdvertisement = (
  source: string,
  props: UsePrioritizedAdvertisementProps = {}
) => {
  const user = useUserProfile();

  const attemptedNetworksRef = useRef<AD_NETWORK[]>([]);
  const currentNetworkRef = useRef<AD_NETWORK | null>(null);
  const [isShowing, setIsShowing] = useState(false);

  const paramsRef = useRef<{ value?: any; innerProps?: UseAdvertisementProps }>({});
  const adsPropsRef = useRef<UseAdvertisementProps | undefined>();

  const getNextNetwork = useCallback(
    (excludeNetworks: AD_NETWORK[]): AD_NETWORK | null => {
      if (user?.avdNetworks && user.avdNetworks.length > 0) {
        const availableNetworks = user.avdNetworks.filter(
          (network) => !excludeNetworks.includes(network)
        );
        return availableNetworks.length > 0 ? availableNetworks[0] : null;
      }
      return null;
    },
    [user?.avdNetworks]
  );

  const resetAdState = () => {
    setIsShowing(false);
    attemptedNetworksRef.current = [];
    currentNetworkRef.current = null;
  };

  const baseShow = useAdvertisement(source, {
    ...props,
    withPriority: true,
    onReward: () => {
      resetAdState();
      adsPropsRef.current?.onReward?.();
    },
    onError: (error) => {
      const currentNetwork = currentNetworkRef.current;

      if (!currentNetwork) {
        setIsShowing(false);
        adsPropsRef.current?.onError?.(error);
        return;
      }

      if (!attemptedNetworksRef.current.includes(currentNetwork)) {
        attemptedNetworksRef.current.push(currentNetwork);
      }

      const nextNetwork = getNextNetwork(attemptedNetworksRef.current);

      if (nextNetwork) {
        currentNetworkRef.current = nextNetwork;
        const { value, innerProps } = paramsRef.current;
        setTimeout(() => baseShow({ value, innerProps, avdNetwork: nextNetwork }), 0);
      } else {
        resetAdState();
        ErrorLogger.error('All ad networks failed', 'use-prioritized-advertisement.ts', {
          attempted: String(attemptedNetworksRef.current),
        });
        showPopup({ message: 'No ads available at the moment' });
        props.onAllNetworksFailed?.();
        adsPropsRef.current?.onError?.(error);
      }
    },
    onSkip: () => {
      resetAdState();
      adsPropsRef.current?.onSkip?.();
    },
  });

  const show = useCallback(
    (value?: any, innerProps?: UseAdvertisementProps) => {
      if (isShowing) return;
      adsPropsRef.current = innerProps;

      paramsRef.current = { value, innerProps };
      attemptedNetworksRef.current = [];
      setIsShowing(true);

      if (user?.avdNetworks && user.avdNetworks.length > 0) {
        const firstNetwork = user.avdNetworks[0];
        currentNetworkRef.current = firstNetwork;
        const { value, innerProps } = paramsRef.current;
        baseShow({ avdNetwork: firstNetwork, value, innerProps });
      }
      // No networks available
      else {
        setIsShowing(false);
        showPopup({ message: 'No ad networks available' });
        props.onAllNetworksFailed?.();
        props.onError?.(new Error('No ad networks available'));
      }
    },
    [baseShow, isShowing, props, user]
  );

  return {
    show,
    isShowing,
  };
};
